import type { Advertisement } from '@/components/sponsorship/AdvertisementColumns'
import {
  AdvertisementStatus,
  API_SPONSORSHIP_STATUS,
  DISPLAY_SPONSORSHIP_STATUS,
  EVENT_STATUS,
  type DisplaySponsorEvent,
  type DisplaySponsorSubEvent,
  type SponsorEventListType,
  type SponsorSubEventListType,
  type SponsorShipAdvertisementType,
  type SponsorShipAPIStatusType,
  type SponsorShipPlanType,
  type AvailableADDatesType,
} from '@/schemas/sponsor.schems'
import { formatDateDisplay } from '@/utils/dateUtils'
import { capitalizeString } from '@/utils/helper'
import { SPONSORSHIP_PLANS } from './sponsorshipPlans'
import type { AvailableDateItem } from '@/utils/dateValidation'

export const sponsorEventListAccessorKey = {
  eventName: 'eventName',
  sponsorshipTier: 'sponsorshipTier',
  startDateTime: 'startDateTime',
  endDateTime: 'endDateTime',
  location: 'location',
  sponsorshipStatus: 'sponsorshipStatus',
}

export const transformSponserEventStatus = ({
  plan,
  status,
}: {
  plan: SponsorShipPlanType | null
  status: SponsorShipAPIStatusType
}) => {
  if (!plan) {
    return DISPLAY_SPONSORSHIP_STATUS.NOT_SELECTED
  }

  switch (status) {
    case API_SPONSORSHIP_STATUS.ACTIVE:
      return DISPLAY_SPONSORSHIP_STATUS.PAID
    case API_SPONSORSHIP_STATUS.PENDING:
      return DISPLAY_SPONSORSHIP_STATUS.SPONSORSHIP_REQUESTED
    case API_SPONSORSHIP_STATUS.CANCELLED:
      return DISPLAY_SPONSORSHIP_STATUS.CANCELLED
    case API_SPONSORSHIP_STATUS.FAILED:
      return DISPLAY_SPONSORSHIP_STATUS.FAILED
    case API_SPONSORSHIP_STATUS.UNDER_PROCESS:
      return DISPLAY_SPONSORSHIP_STATUS.SPONSORSHIP_REQUESTED
  }
}

export const transformSponsorEventListData = (
  data: SponsorEventListType
): DisplaySponsorEvent => {
  return {
    id: data?.id,
    eventName: data?.event?.name,
    location: data?.event?.venueAddress ?? 'Online',
    sponsorshipStatus: transformSponserEventStatus({
      plan: data?.plan,
      status: data?.status,
    }),
    sponsorshipTier: data?.plan?.name || '-',
    startDateTime: formatDateDisplay(data?.event?.startDateTime),
    endDateTime: formatDateDisplay(data?.event?.endDateTime),
    eventStatus: data?.event?.status,
  }
}

export const transformSponsorSubEventListData = (
  data: SponsorSubEventListType
): DisplaySponsorSubEvent => {
  const locationText =
    data?.event?.location === 'online'
      ? 'Online'
      : data?.event?.venueAddress || 'Offline'

  return {
    id: data?.id,
    subEventName: data?.subEvent?.name,
    eventName: data?.event?.name,
    location: locationText,
    sponsorshipStatus: transformSponserEventStatus({
      plan: data?.plan,
      status: data?.status,
    }),
    sponsorshipTier: data?.plan?.name || '-',
    startDateTime: formatDateDisplay(data?.event?.startDateTime),
    endDateTime: formatDateDisplay(data?.event?.endDateTime),
    eventStatus: data?.event?.status,
  }
}

export const fileTypes = ['image/jpeg', 'image/png', 'image/svg+xml']
export const eventImageVideoTypes = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-msvideo',
]
export const eventImageSize = 5 * 1024 * 1024
export const sponsorLogoSize = 150 * 1024

export const transformAdvertisementData = (
  ad: SponsorShipAdvertisementType
): Advertisement => {
  return {
    id: ad.id,
    sponsorShipId: ad.sponsorshipId,
    adName: ad.name,
    sponsorName: `${ad.sponsor?.firstName} ${ad.sponsor?.lastName}`,
    eventName: ad.event?.name,
    description: ad.description,
    placement: ad.placement,
    duration: `${formatDateDisplay(ad.startDate)} - ${formatDateDisplay(ad.endDate)}`,
    status: ad.status,
    performance: {
      impressions: ad.totalImpressions,
      clicks: ad.totalClicks,
      ctr: ad.ctr,
    },
    isChangesRequired: ad.isChangesRequired,
    sponsorId: ad.sponsorId,
    subEventName: ad.subEvent?.name,
  }
}
export const getAdvertisementStatusBadgeProps = (
  status: string,
  isChangesRequired?: boolean
) => {
  if (isChangesRequired) {
    return {
      className: 'bg-ss-warning-50 text-ss-warning-200 border-ss-warning-100',
      children: 'Pending Review',
    }
  }

  switch (status) {
    case AdvertisementStatus.ACTIVE:
      return {
        className:
          'bg-ss-positive-50 text-ss-positive-200 border-ss-positive-100',
        children: 'Active',
      }
    case AdvertisementStatus.INACTIVE:
      return {
        className: 'bg-ss-warning-50 text-ss-warning-200 border-ss-warning-100',
        children: 'Paused',
      }
    case AdvertisementStatus.EXPIRED:
      return {
        className:
          'bg-ss-critical-50 text-ss-critical-200 border-ss-critical-100',
        children: 'Expired',
      }
    case AdvertisementStatus.DRAFT:
      return {
        className:
          'bg-ss-action-gray-50 text-ss-neutral-600 border-ss-neutral-200',
        children: 'Pending Approval',
      }
    default:
      return {
        className:
          'bg-ss-action-gray-50 text-ss-neutral-600 border-ss-neutral-200',
        children: capitalizeString(status),
      }
  }
}

export const AdvertisementStatusMap = {
  Active: 'active' as const,
  Paused: 'inactive' as const,
  Expired: 'expired' as const,
  Draft: 'draft' as const,
  Rejected: 'rejected' as const,
  'Pending Review': 'pending_review' as const,
}

export const advertisementSortinngColumnsNames = {
  duration: 'duration',
  startDate: 'startDate',
}

export const AdvertisementActions = {
  active: 'active' as const,
  inactive: 'inactive' as const,
  delete: 'delete' as const,
  approve: 'approve' as const,
  reject: 'reject' as const,
}
export type AdvertisementActionsType =
  (typeof AdvertisementActions)[keyof typeof AdvertisementActions]

export interface UpdateAdvertisementPlacementPayload {
  startDate: Date | string
  endDate: Date | string
  placement: string
}

export const getListOfkeyBenefits = (
  sponsorShippackage: string,
  heroDuration: number | string = 0
) => {
  switch (sponsorShippackage) {
    case SPONSORSHIP_PLANS.PRESENTING: {
      return [
        'In-Feed Bursts Ads (50000 impressions)',
        'Right-Rail Ads (100000 impressions)',
      ]
    }
    case SPONSORSHIP_PLANS.PROGRAM_OWNER: {
      return [
        'In-Feed Bursts Ads (50000 impressions)',
        'Right-Rail Ads (75000 impressions)',
      ]
    }
    case SPONSORSHIP_PLANS.SUPPORTING: {
      return [
        'In-Feed Bursts Ads (25000 impressions)',
        'Right-Rail Ads (40000 impressions)',
      ]
    }
    case SPONSORSHIP_PLANS.NAMING_SPONSOR: {
      return [
        `Hero Flights Ad ( Event page ${heroDuration} Days)`,
        'Exclusive title rights: “[Brand Name] Presents The Black Powder Festival & Black Diamond Originals.”',
        'Premier visibility across all marketing, media, and on-site assets.',
        'Custom branded activation zone + on-stage acknowledgments.',
        'Full SlopeSync Metrics Dashboard access and post-event case study.',
      ]
    }
    default: {
      return []
    }
  }
}
export const AdvertisementPlacements = {
  HERO: 'hero',
  IN_FEED_BURSTS: 'in_feed_bursts',
  RIGHT_RAIL: 'right_rail',
}

export const transformAdvertisementPlacement = (placement: string) => {
  switch (placement) {
    case AdvertisementPlacements.HERO: {
      return 'Hero Flight'
    }
    case AdvertisementPlacements.IN_FEED_BURSTS: {
      return 'In-Feed Bursts'
    }
    case AdvertisementPlacements.RIGHT_RAIL: {
      return 'Right-Rail'
    }
    default: {
      return placement
    }
  }
}

export const SPONSOR_REPORT_TYPES = {
  LAST_10_DAYS: 'last-10-days',
  LAST_30_DAYS: 'last-30-days',
  LAST_90_DAYS: 'last-90-days',
  ALL_TIME: 'all-time',
} as const

export const SPONSOR_TAB = {
  ADVERTISEMENT: 'advertisements' as const,
  DEMOGRAPHICS: 'demographics' as const,
}

export const isEventCompleted = (status: string): boolean => {
  return status === EVENT_STATUS.COMPLETED
}

export const EVENT_MEDIA_OPTIONS = {
  IMAGE: 'image' as const,
  VIDEO: 'video' as const,
}

export type EventMediaType =
  (typeof EVENT_MEDIA_OPTIONS)[keyof typeof EVENT_MEDIA_OPTIONS]

export const getFileExtension = (path: string): string => {
  if (!path || typeof path !== 'string') {
    return ''
  }

  const filename = path.split('?')[0].split('/').pop() || ''
  const lastDotIndex = filename.lastIndexOf('.')

  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    return ''
  }

  return filename.substring(lastDotIndex + 1).toLowerCase()
}

export const videoExtensions = ['mp4', 'mov', 'webm', 'avi']
export const imageExtensions = ['jpg', 'jpeg', 'png', 'svg', 'webp']
export const gifExtension = 'gif'

export const isVideoFile = (path: string): boolean => {
  return videoExtensions.includes(getFileExtension(path))
}

export const isGifFile = (path: string): boolean => {
  return getFileExtension(path) === gifExtension
}

export const isStaticImageFile = (path: string): boolean => {
  return imageExtensions.includes(getFileExtension(path))
}

export const getMediaType = (
  path: string
): 'image' | 'video' | 'gif' | 'unknown' => {
  if (isVideoFile(path)) return 'video'
  if (isGifFile(path)) return 'gif'
  if (isStaticImageFile(path)) return 'image'
  return 'unknown'
}

export const ADID_PARAM = 'adId'
export const MAX_SUBJECT_LENGTH = 255
export const MAX_DESCRIPTION_LENGTH = 500
export const REQUEST_MODIFICATION_FIELDS = {
  SUBJECT: 'subject',
  DESCRIPTION: 'description',
}

export const AD_SLOT_OPTIONS = [
  { id: AdvertisementPlacements.HERO, name: 'Hero ads', title: 'Hero ads' },
  {
    id: AdvertisementPlacements.RIGHT_RAIL,
    name: 'Rail ads',
    title: 'Rail ads',
  },
  {
    id: AdvertisementPlacements.IN_FEED_BURSTS,
    name: 'In-feed ads',
    title: 'In-feed ads',
  },
]

const toLocalDateString = (utcDate: Date): string => {
  const year = utcDate.getFullYear()
  const month = String(utcDate.getMonth() + 1).padStart(2, '0')
  const day = String(utcDate.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const getNormalizedAdDates = (
  availableADDatedata: AvailableADDatesType
) => {
  if (!availableADDatedata) return undefined

  const { bookedDates } = availableADDatedata

  // Convert booked dates to local date strings
  const bookedDateStrings = bookedDates.map(utcDateString => {
    const utcDate = new Date(utcDateString)
    return toLocalDateString(utcDate)
  })

  // Return only the booked dates as unavailable
  // This allows the date picker to enable all future dates except booked ones
  const dates: AvailableDateItem[] = bookedDateStrings.map(dateString => ({
    date: dateString,
    available: false,
  }))

  return dates
}
